import React, { useMemo } from 'react';
import { Box, Typography, Card, CardContent, Grid, Divider } from '@mui/material';
import { formatDateBr as formatDateBrGlobal } from '../../utils/formatters/dateUtils';
import { formatCep } from '../../utils/formatters/stringUtils';
import { useReportConfig } from '../../contexts/ReportConfigContext.jsx';
import ReportStyledField from './ReportStyledField.jsx';
import ReportWidget from './ReportWidget';

const safeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatArea = (value) => {
  const n = safeNumber(value);
  if (n === null) return '-';
  return `${n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ha`;
};

const formatPercent = (value) => {
  const n = safeNumber(value);
  if (n === null) return '-';
  return `${n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} %`;
};

const formatBooleanSimNao = (value) => {
  if (value === true) return 'Sim';
  if (value === false) return 'Não';
  return '-';
};

const formatDateBr = (value) => {
  if (!value) return '-';
  const formatted = formatDateBrGlobal(value);
  return formatted || String(value);
};

const formatGeneric = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const normalizeCafirData = (cafirRaw) => {
  if (!cafirRaw) return [];

  if (Array.isArray(cafirRaw)) {
    return cafirRaw.filter((item) => item && typeof item === 'object');
  }

  if (cafirRaw.details && Array.isArray(cafirRaw.details)) {
    return cafirRaw.details.filter((item) => item && typeof item === 'object');
  }

  if (typeof cafirRaw === 'object') {
    return [cafirRaw];
  }

  return [];
};

function CafirTitularPrintCard({ titular }) {
  const { isFieldEnabled, getFieldLabel } = useReportConfig();

  if (!titular || typeof titular !== 'object') return null;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        bgcolor: 'grey.50',
      }}
    >
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {formatGeneric(titular.nome) || 'Titular'}
        </Typography>
        <Grid container spacing={1}>
          {isFieldEnabled('cafir', 'titulares', 'cpf_cnpj') && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {getFieldLabel('cafir', 'cpf_cnpj', 'CPF/CNPJ')}
              </Typography>
              <ReportStyledField
                domainKey="cafir"
                fieldKey="cpf_cnpj"
                variant="body2"
                defaultSx={{ fontWeight: 500 }}
              >
                {formatGeneric(titular.cpf_cnpj)}
              </ReportStyledField>
            </Grid>
          )}
          {isFieldEnabled('cafir', 'titulares', 'percentual') && (
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {getFieldLabel('cafir', 'percentual', 'Percentual')}
              </Typography>
              <ReportStyledField
                domainKey="cafir"
                fieldKey="percentual"
                variant="body2"
                defaultSx={{ fontWeight: 500 }}
              >
                {formatPercent(titular.percentual)}
              </ReportStyledField>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}

export default function ReportPrintCafirDetails({ snapshot }) {
  const { isDomainEnabled, isSectionEnabled, isFieldEnabled, getFieldLabel } = useReportConfig();

  if (!isDomainEnabled('cafir')) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Domínio CAFIR desabilitado na configuração do relatório.
        </Typography>
      </Box>
    );
  }

  const properties =
    snapshot && snapshot.properties && typeof snapshot.properties === 'object'
      ? snapshot.properties
      : {};

  const cafirRaw = properties.cafir;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const records = useMemo(() => normalizeCafirData(cafirRaw), [cafirRaw]);

  if (!records.length) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Nenhum registro CAFIR encontrado para este relatório.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        CAFIR - Cadastro Fiscal de Imóveis Rurais
      </Typography>

      {records.map((cafir, index) => {
        const nirf =
          cafir.nirf ||
          cafir.NIRF ||
          cafir.codigo_nirf ||
          cafir.codigo_cafir ||
          cafir.codigo ||
          cafir.codigo_incra;

        const titulares = Array.isArray(cafir.titulares) ? cafir.titulares : [];

        return (
          <Card
            key={nirf || index}
            sx={{
              mb: 2,
              borderRadius: 2,
              borderLeft: 4,
              borderLeftColor: 'info.main',
              boxShadow: 1,
            }}
          >
            <CardContent>
              {isSectionEnabled('cafir', 'dados_imovel') && (
                <ReportWidget sectionKey="cafir" widgetKey="cafir_dados_imovel">
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Informações do Imóvel
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {isFieldEnabled('cafir', 'dados_imovel', 'codigo_incra') && (
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('cafir', 'codigo_incra', 'Código INCRA')}
                        </Typography>
                        <ReportStyledField
                          domainKey="cafir"
                          fieldKey="codigo_incra"
                          variant="body2"
                          defaultSx={{ fontWeight: 500, wordBreak: 'break-all' }}
                        >
                          {formatGeneric(cafir.codigo_incra)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('cafir', 'dados_imovel', 'nirf') && (
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('cafir', 'nirf', 'NIRF')}
                        </Typography>
                        <ReportStyledField
                          domainKey="cafir"
                          fieldKey="nirf"
                          variant="body2"
                          defaultSx={{ fontWeight: 500, wordBreak: 'break-all' }}
                        >
                          {formatGeneric(nirf)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('cafir', 'dados_imovel', 'nome_da_propriedade') && (
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('cafir', 'nome_da_propriedade', 'Nome da Propriedade')}
                        </Typography>
                        <ReportStyledField
                          domainKey="cafir"
                          fieldKey="nome_da_propriedade"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(cafir.nome_da_propriedade || cafir.nome)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('cafir', 'dados_imovel', 'uf') && (
                      <Grid item xs={6} md={2}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('cafir', 'uf', 'UF')}
                        </Typography>
                        <ReportStyledField
                          domainKey="cafir"
                          fieldKey="uf"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(cafir.uf)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('cafir', 'dados_imovel', 'municipio') && (
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('cafir', 'municipio', 'Município')}
                        </Typography>
                        <ReportStyledField
                          domainKey="cafir"
                          fieldKey="municipio"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(cafir['município'] || cafir.municipio)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('cafir', 'dados_imovel', 'area_da_propriedade') && (
                      <Grid item xs={6} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('cafir', 'area_da_propriedade', 'Área da Propriedade')}
                        </Typography>
                        <ReportStyledField
                          domainKey="cafir"
                          fieldKey="area_da_propriedade"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatArea(cafir['área_da_propriedade'] || cafir.area)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('cafir', 'dados_imovel', 'logradouro') && (
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('cafir', 'logradouro', 'Logradouro')}
                        </Typography>
                        <ReportStyledField
                          domainKey="cafir"
                          fieldKey="logradouro"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(cafir.logradouro)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('cafir', 'dados_imovel', 'distrito') && (
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('cafir', 'distrito', 'Distrito')}
                        </Typography>
                        <ReportStyledField
                          domainKey="cafir"
                          fieldKey="distrito"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(cafir.distrito)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('cafir', 'dados_imovel', 'cep') && (
                      <Grid item xs={6} md={2}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('cafir', 'cep', 'CEP')}
                        </Typography>
                        <ReportStyledField
                          domainKey="cafir"
                          fieldKey="cep"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatCep(cafir.cep)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('cafir', 'dados_imovel', 'data_da_atualizacao') && (
                      <Grid item xs={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('cafir', 'data_da_atualizacao', 'Data da Atualização')}
                        </Typography>
                        <ReportStyledField
                          domainKey="cafir"
                          fieldKey="data_da_atualizacao"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatDateBr(
                            cafir['data_da_atualização'] ||
                              cafir.data_da_atualizacao ||
                              cafir.data_atualizacao,
                          )}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('cafir', 'dados_imovel', 'imovel_isento') && (
                      <Grid item xs={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('cafir', 'imovel_isento', 'Imóvel Isento')}
                        </Typography>
                        <ReportStyledField
                          domainKey="cafir"
                          fieldKey="imovel_isento"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatBooleanSimNao(cafir['imóvel_isento'] || cafir.imovel_isento)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('cafir', 'dados_imovel', 'codigo_sncr') && (
                      <Grid item xs={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('cafir', 'codigo_sncr', 'Código SNCR')}
                        </Typography>
                        <ReportStyledField
                          domainKey="cafir"
                          fieldKey="codigo_sncr"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(cafir['código_sncr'] || cafir.codigo_sncr)}
                        </ReportStyledField>
                      </Grid>
                    )}
                  </Grid>
                </ReportWidget>
              )}

              {titulares.length > 0 && isSectionEnabled('cafir', 'titulares') && (
                <ReportWidget sectionKey="cafir" widgetKey="cafir_titulares">
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Titulares ({titulares.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {titulares.map((titular, tIndex) => (
                      <Grid item xs={12} md={6} key={tIndex}>
                        <CafirTitularPrintCard titular={titular} />
                      </Grid>
                    ))}
                  </Grid>
                </ReportWidget>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}
